import React, { useCallback } from 'react';

import { ButtonBase } from '@mui/material';
import { Plus } from 'lucide-react';
import { AlineTooltip } from 'src/components/generic';
import { makeStyles } from 'tss-react/mui';

const DASH_LENGTH_PX = 6;
const CONNECTOR_HEIGHT_PX = 40;

function getDashedLine(color: string): string {
  return `repeating-linear-gradient(to bottom, ${color} 0 ${DASH_LENGTH_PX}px, transparent ${DASH_LENGTH_PX}px ${DASH_LENGTH_PX * 2}px)`;
}

const useStyles = makeStyles<void, 'addButton'>()((theme, _params, classes) => ({
  addButton: {
    '&:focus-visible': {
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: theme.appColors.primary,
      borderColor: theme.appColors.primary,
      color: theme.palette.common.white,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: '50%',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    display: 'flex',
    height: 20,
    justifyContent: 'center',
    opacity: 0,
    padding: 0,
    position: 'relative',
    transition: 'opacity 0.15s ease, background-color 0.15s ease, border-color 0.15s ease',
    width: 20,
  },
  line: {
    backgroundImage: getDashedLine(theme.appColors.rule),
    height: '100%',
    left: '50%',
    position: 'absolute',
    top: 0,
    transform: 'translateX(-50%)',
    width: 1,
  },
  lineActive: {
    backgroundImage: getDashedLine(theme.appColors.primary),
  },
  root: {
    [`&:hover .${classes.addButton}`]: {
      opacity: 1,
    },
    alignItems: 'center',
    display: 'flex',
    height: CONNECTOR_HEIGHT_PX,
    justifyContent: 'center',
    /**
     * The phases list puts a flex gap on either side of this row; cancelling it out
     * keeps the gutter between two cards exactly one connector tall.
     */
    marginBlock: theme.spacing(-2),
    position: 'relative',
    width: '100%',
  },
}));

export interface BetweenPhasesConnectorProps {
  insertOrder: number;
  isAfterActivePhase: boolean;
  onAddPhase: (order: number) => void;
}

export const BetweenPhasesConnector = React.memo(function BetweenPhasesConnector({
  insertOrder,
  isAfterActivePhase,
  onAddPhase,
}: BetweenPhasesConnectorProps) {
  const { classes, cx } = useStyles();

  const handleAddPhase = useCallback(() => {
    onAddPhase(insertOrder);
  }, [insertOrder, onAddPhase]);

  return (
    <div className={classes.root}>
      <span aria-hidden className={cx(classes.line, isAfterActivePhase && classes.lineActive)} />
      <AlineTooltip arrow placement="top" title="Add new phase">
        <ButtonBase
          aria-label="Add new phase"
          className={classes.addButton}
          onClick={handleAddPhase}
        >
          <Plus size={12} strokeWidth={2.5} />
        </ButtonBase>
      </AlineTooltip>
    </div>
  );
});
